import { Spin } from 'antd'
import React from 'react'
import styled from 'styled-components'
import { Text } from 'sutra/components/Text/Text'
import { gray } from 'sutra/tokens/colors'
import { spacing } from 'sutra/tokens/spacing'

import type { FilterOption } from 'features/filters/types'
import type { InsightsRecency } from 'features/trend-detection/features/issue-agent-detail/components/ActivityFeed/ActivityFeed'
import { ActivityFeed } from 'features/trend-detection/features/issue-agent-detail/components/ActivityFeed/ActivityFeed'
import { AgentInfoCard } from 'features/trend-detection/features/issue-agent-detail/components/AgentInfoCard/AgentInfoCard'
import type { DetailHeaderHandlers } from 'features/trend-detection/features/issue-agent-detail/components/DetailHeader/DetailHeader'
import { DetailHeader } from 'features/trend-detection/features/issue-agent-detail/components/DetailHeader/DetailHeader'
import { NotificationsCard } from 'features/trend-detection/features/issue-agent-detail/components/NotificationsCard/NotificationsCard'
import { FrameInspectIcon } from 'features/trend-detection/features/issue-agent-detail/components/SectionIcons'
import type {
  IssueAgentResponse,
  IssueAgentRunResponse,
  SavedFilters,
} from 'features/trend-detection/types'

export interface IssueAgentDetailViewHandlers extends DetailHeaderHandlers {
  getInsightsHref: (recency: InsightsRecency) => string
}

export interface IssueAgentDetailViewEmailToggle {
  checked: boolean
  onChange: (checked: boolean) => void
  disabled?: boolean
}

export interface IssueAgentDetailViewProps {
  agent: IssueAgentResponse
  runs: IssueAgentRunResponse[]
  runsLoading: boolean
  latestInsightsCount: number
  handlers: IssueAgentDetailViewHandlers
  /** Resolves UUID filter values to pretty names in the Monitoring Scope chips. */
  filterOptions?: FilterOption[]
  emailToggle: IssueAgentDetailViewEmailToggle
}

export const IssueAgentDetailView: React.FC<IssueAgentDetailViewProps> = ({
  agent,
  runs,
  runsLoading,
  latestInsightsCount,
  handlers,
  filterOptions,
  emailToggle,
}) => {
  const instructions = agent.description || agent.config.prompt || null
  const saved = agent.config.filters as SavedFilters | undefined
  const criteria = saved?.criteria ?? []
  return (
    <PageContainer>
      <DetailHeader agent={agent} handlers={handlers} />
      <Body>
        <ActivityFeed
          runs={runs}
          isLoading={runsLoading}
          latestInsightsCount={latestInsightsCount}
          getInsightsHref={handlers.getInsightsHref}
          onEditFilters={
            agent.is_editor && !agent.is_default ? handlers.onEdit : undefined
          }
        />
        <RightColumn>
          <AgentInfoCard
            header={{ label: 'Agent details' }}
            instructions={instructions}
            sections={[
              {
                icon: <FrameInspectIcon />,
                title: 'Monitoring Scope',
                filters: criteria,
                emptyText: 'All feedback',
              },
            ]}
            filterOptions={filterOptions}
          />
          <NotificationsCard
            checked={emailToggle.checked}
            onChange={emailToggle.onChange}
            disabled={emailToggle.disabled}
          />
        </RightColumn>
      </Body>
    </PageContainer>
  )
}

export const IssueAgentDetailViewLoading: React.FC = () => (
  <Centered>
    <Spin size="large" />
  </Centered>
)

export const IssueAgentDetailViewNotFound: React.FC = () => (
  <Centered>
    <Text $size="md">Agent not found.</Text>
  </Centered>
)

const PageContainer = styled.div`
  padding: ${spacing[6]}px ${spacing[8]}px;
`

const Body = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing[6]}px;
  align-items: flex-start;
`

const RightColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[6]}px;
  flex-shrink: 0;
`

const Centered = styled.div`
  padding: ${spacing[16]}px 0;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 320px;
  color: ${gray[600]};
`
